import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import RolesContext from 'contexts/RolesContext';
import Row from 'components/Common/Row';
import DragHandle from 'components/DragAndDropList/DragHandle';
import DragAndDropListContext from 'components/DragAndDropList/DragAndDropListContext';
import PermissionGates from 'components/PermissionGates';

import ContextMenu, { Option } from 'kingpin/atoms/ContextMenu';
import Typography from 'kingpin/atoms/Typography';
import PERFORMANCE_BOARD_PERMISSIONS from 'permissionDefinitions/performanceBoard';
import PERMISSIONS from 'permissionDefinitions';
import Column from 'screens/DataManager/DatasetDefinitions/Column';

import sectionTypeCheckers from '../sectionTypeCheckers';
import RecognitionPaymentsHeading from '../Sections/RecognitionPayments/RecognitionPaymentsHeading';
import ScoringRewardHeading from '../../../DriverAX/components/BonusReward/BonusRewardHeading';
import BonusSubPeriodRewardHeading from './BonusSubPeriodRewardHeading';
import useHasBonusSubPeriods from '../../hooks/useHasBonusSubPeriods';

const DraggingMixin = css`
  border-radius: 4px;
  box-shadow: 0 3px 8px 3px rgba(53, 45, 67, 0.07);
  margin-bottom: 16px;
`;

const ItemDraggingMixin = css`
  border: 1px solid var(--color-info-v2);
`;

const AbsoluteHeaderMixin = css`
  position: absolute;
  width: 100%;
  pointer-events: none;
`;

const SectionHeaderOuterDiv = styled.div<{
  isDragging: boolean;
  isItemDragging: boolean;
}>`
  position: relative;
  ${(props) => props.isDragging && DraggingMixin}
  ${(props) => props.isItemDragging && ItemDraggingMixin}
`;

const SectionHeaderInnerDiv = styled.div<{ isAbsolute: boolean }>`
  ${(props) => props.isAbsolute && AbsoluteHeaderMixin}
`;

const SectionTitle = ({
  section,
  isDragging,
}: {
  section: PerformanceBoardTypes.SlideOut.SlideOutSection;
  isDragging: boolean;
}) => {
  const hasSubPeriods = useHasBonusSubPeriods();

  if (sectionTypeCheckers.isBonusRewardSection(section)) {
    // Sub-period programmes show the reward name + period total here (in place of
    // the single-week reward heading), with the weekly fact table beneath.
    if (hasSubPeriods && !isDragging) {
      return (
        <BonusSubPeriodRewardHeading
          bonusConfigId={section.bonusConfigId}
          rewardName={section.rewardName}
          totalValueField={section.totalValueField}
        />
      );
    }
    return <ScoringRewardHeading rewardName={section.rewardName} />;
  }

  if (
    sectionTypeCheckers.isRecognitionPaymentsSection(section) &&
    !isDragging
  ) {
    return <RecognitionPaymentsHeading title={section.title} />;
  }

  if (
    sectionTypeCheckers.isBonusTotalRewardSection(section) ||
    sectionTypeCheckers.isBonusEligibilitySection(section)
  ) {
    if (isDragging) {
      return <Typography.Header type={'H5'}>{section.title}</Typography.Header>;
    }

    return null;
  }

  return <Typography.Header type={'H5'}>{section.title}</Typography.Header>;
};

const SectionHeader = ({
  title,
  onRemoveClicked,
  onEditClicked,
  isHovering,
  section,
}: {
  title: string;
  onRemoveClicked: () => void;
  onEditClicked: () => void;
  isHovering: boolean;
  section: PerformanceBoardTypes.SlideOut.SlideOutSection;
}) => {
  const { isDragging } = useContext(DragAndDropListContext);
  const [isItemDragging, setIsItemDragging] = useState<boolean>(false);

  useEffect(() => {
    if (!isDragging) {
      setIsItemDragging(false);
    }
  }, [isDragging]);

  const isAbsolute = useMemo(() => {
    if (isDragging) {
      return false;
    }

    return (
      sectionTypeCheckers.isBonusEligibilitySection(section) ||
      sectionTypeCheckers.isBonusTotalRewardSection(section)
    );
  }, [isDragging, section]);

  const { currentPermissions } = useContext(RolesContext);

  const hasEditPermission = currentPermissions.includes(
    PERFORMANCE_BOARD_PERMISSIONS.MODIFY_WIDGETS,
  );
  return (
    <SectionHeaderOuterDiv
      isItemDragging={isItemDragging}
      isDragging={isDragging}
    >
      <SectionHeaderInnerDiv isAbsolute={isAbsolute}>
        <Row spaceBetween centerAlign>
          <Column style={{ minHeight: 42 }}>
            <div
              style={{
                pointerEvents: 'auto',
                padding: '0px 4px',
                visibility:
                  (isHovering || isDragging) && hasEditPermission
                    ? 'visible'
                    : 'hidden',
              }}
            >
              <DragHandle
                noMargin
                testId="testWidget"
                setIsItemDragging={setIsItemDragging}
              />
            </div>
            <div style={{ marginRight: 8, flex: 1 }}>
              <SectionTitle section={section} isDragging={isDragging} />
            </div>
          </Column>
          <Row centerAlign>
            <PermissionGates.Has
              requiredPermission={PERMISSIONS.PERFORMANCE_BOARD.MODIFY_WIDGETS}
            >
              <div
                style={{
                  pointerEvents: 'auto',
                  marginRight: '12px',
                  visibility: isHovering || isDragging ? 'visible' : 'hidden',
                }}
              >
                <ContextMenu testId={`${title}-testId`} icon="dots-menu">
                  <>
                    {title !== 'Comments' && (
                      <Option
                        label="Edit"
                        icon="edit-filled"
                        onClick={onEditClicked}
                      />
                    )}
                  </>
                  <Option
                    label="Delete"
                    icon="cross"
                    onClick={onRemoveClicked}
                  />
                </ContextMenu>
              </div>
            </PermissionGates.Has>
          </Row>
        </Row>
      </SectionHeaderInnerDiv>
    </SectionHeaderOuterDiv>
  );
};

export default SectionHeader;
